package cz.lhoracek.mapl.data.repository

import cz.lhoracek.mapl.data.model.Player
import cz.lhoracek.mapl.data.model.Round
import cz.lhoracek.mapl.data.model.Session
import cz.lhoracek.mapl.data.model.SessionStatus
import cz.lhoracek.mapl.data.model.SessionWithPlayers
import cz.lhoracek.mapl.domain.normalizeBetToPowerOfTwo
import cz.lhoracek.mapl.domain.ScoreCalculator
import cz.lhoracek.mapl.domain.SessionScoreCalculator
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Source
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.firestoreSettings
import dev.gitlive.firebase.firestore.persistentCacheSettings
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch

class FirestoreSessionRepository(
    private val knownSessionIds: KnownSessionIds,
) : SessionRepository {

    private val db = Firebase.firestore

    init {
        // Explicitly enable persistent local cache for offline-first behavior.
        db.settings = firestoreSettings {
            cacheSettings = persistentCacheSettings { }
        }
    }

    // Writes are fire-and-forget: the GitLive SDK's .set()/.update() await
    // server ACK, not just local cache commit. This scope lets us queue writes
    // without blocking the caller — Firestore's offline persistence will sync
    // them when connectivity is restored.
    private val writeScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    // ── Collection helpers ───────────────────────────────────────────────────

    private fun sessionDoc(id: String) = db.collection("sessions").document(id)
    private fun playersCol(sessionId: String) = sessionDoc(sessionId).collection("players")
    private fun roundsCol(sessionId: String) = sessionDoc(sessionId).collection("rounds")

    // ── Observe ──────────────────────────────────────────────────────────────

    override fun observeKnownSessionIds(): Flow<List<String>> = knownSessionIds.ids

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeSessions(): Flow<List<Session>> =
        knownSessionIds.ids.flatMapLatest { ids ->
            if (ids.isEmpty()) return@flatMapLatest flowOf(emptyList())

            combine(
                ids.map { id ->
                    sessionDoc(id).snapshots.map { snap ->
                        if (!snap.exists) return@map null
                        runCatching { snap.data<SessionFirestore>().toSession(id) }
                            .getOrNull()
                    }
                }
            ) { sessions ->
                sessions.filterNotNull().sortedByDescending { it.createdAt }
            }
        }

    override fun observeSessionWithPlayers(sessionId: String): Flow<SessionWithPlayers?> {
        val sessionFlow = sessionDoc(sessionId).snapshots.map { snap ->
            if (!snap.exists) return@map null
            snap.data<SessionFirestore>().toSession(sessionId)
        }
        val playersFlow = playersCol(sessionId).snapshots.map { snap ->
            snap.documents.map { it.data<PlayerFirestore>().toPlayer(it.id) }
                .sortedBy { it.name }
        }
        val roundsFlow = roundsCol(sessionId).snapshots.map { snap ->
            snap.documents
                .map { it.data<RoundFirestore>().toRound(it.id, sessionId) }
                .sortedBy { it.timestamp }
        }
        return combine(sessionFlow, playersFlow, roundsFlow) { session, players, rounds ->
            session?.let {
                SessionWithPlayers(it, players, rounds)
            }
        }
    }

    override fun observeRounds(sessionId: String): Flow<List<Round>> =
        roundsCol(sessionId).snapshots.map { snap ->
            snap.documents
                .map { it.data<RoundFirestore>().toRound(it.id, sessionId) }
                .sortedBy { it.timestamp }
        }

    // ── Mutate ───────────────────────────────────────────────────────────────

    override suspend fun createSession(
        name: String,
        players: List<Player>,
        baseBet: Int,
        potEnabled: Boolean,
    ): String {
        val id = generateUuid()
        val now = currentTimeMillis()

        // WriteBatch is atomic in the local cache — session + all players land
        // together or not at all. commit() still awaits server ACK, so we
        // launch it in writeScope to avoid blocking the caller offline.
        writeScope.launch {
            val batch = db.batch()
            batch.set(
                sessionDoc(id),
                SessionFirestore(
                    name = name,
                    baseBet = baseBet,
                    currentBet = baseBet,
                    potEnabled = potEnabled,
                    potAmount = 0,
                    status = "ACTIVE",
                    createdAt = now.toDouble(),
                    playerIds = players.map { it.id },
                )
            )
            players.forEach { player ->
                batch.set(
                    playersCol(id).document(player.id),
                    PlayerFirestore(name = player.name, color = player.color, score = player.score)
                )
            }
            batch.commit()
        }

        // Persist known ID in background as well so session creation returns
        // immediately even when local DataStore I/O is slow.
        writeScope.launch {
            runCatching { knownSessionIds.add(id) }
        }
        return id
    }

    override suspend fun joinSession(sessionId: String): Boolean {
        // 1) Cache first for instant offline success when already locally known.
        val cachedExists = runCatching { sessionDoc(sessionId).get(Source.CACHE).exists }
            .getOrDefault(false)
        if (cachedExists) {
            knownSessionIds.add(sessionId)
            return true
        }

        // 2) Server check when available.
        return try {
            val serverSnap = sessionDoc(sessionId).get(Source.SERVER)
            if (!serverSnap.exists) return false
            knownSessionIds.add(sessionId)
            true
        } catch (_: Exception) {
            // 3) Off-grid: persist known ID so UI can show placeholder row until
            // details are fetched later when connectivity returns.
            knownSessionIds.add(sessionId)
            true
        }
    }

    override suspend fun addRound(sessionId: String, round: Round) {
        // Source.CACHE avoids the server round-trip that would hang when offline.
        // The real-time listener keeps cache current when online, so cache reads
        // are always up-to-date in practice.
        val session = sessionDoc(sessionId).get(Source.CACHE).data<SessionFirestore>().toSession(sessionId)
        val players = playersCol(sessionId).get(Source.CACHE).documents
            .map { it.data<PlayerFirestore>().toPlayer(it.id) }
        val currentBet = session.currentBet
        val storedRound = round.copy(
            sessionId = sessionId,
            bet = if (round.bet > 0) round.bet else currentBet,
        )

        if (session.potEnabled) {
            val rounds = roundsCol(sessionId).get(Source.CACHE).documents
                .map { it.data<RoundFirestore>().toRound(it.id, sessionId) }
            val updatedRounds = rounds + storedRound
            val recalculated = SessionScoreCalculator.calculate(
                session = session,
                players = players,
                rounds = updatedRounds,
            )

            writeScope.launch {
                roundsCol(sessionId).document(storedRound.id).set(RoundFirestore.fromRound(storedRound))
                players.forEach { player ->
                    val newScore = recalculated.scores[player.id] ?: 0
                    if (newScore != player.score) {
                        playersCol(sessionId).document(player.id).updateFields {
                            "score" to newScore
                        }
                    }
                }
                sessionDoc(sessionId).updateFields { "potAmount" to recalculated.currentPot }
            }
            return
        }

        val deltas = ScoreCalculator.calculateDeltas(storedRound, players, currentBet)

        writeScope.launch {
            roundsCol(sessionId).document(storedRound.id).set(RoundFirestore.fromRound(storedRound))
            players.forEach { player ->
                val delta = deltas[player.id] ?: 0
                if (delta != 0) {
                    playersCol(sessionId).document(player.id).updateFields {
                        "score" to (player.score + delta)
                    }
                }
            }
        }
    }

    override suspend fun deleteRound(sessionId: String, round: Round) {
        val session = sessionDoc(sessionId).get(Source.CACHE).data<SessionFirestore>().toSession(sessionId)
        val players = playersCol(sessionId).get(Source.CACHE).documents
            .map { it.data<PlayerFirestore>().toPlayer(it.id) }

        if (session.potEnabled) {
            val rounds = roundsCol(sessionId).get(Source.CACHE).documents
                .map { it.data<RoundFirestore>().toRound(it.id, sessionId) }
            val updatedRounds = rounds.filter { it.id != round.id }
            val recalculated = SessionScoreCalculator.calculate(
                session = session,
                players = players,
                rounds = updatedRounds,
            )

            writeScope.launch {
                roundsCol(sessionId).document(round.id).delete()
                players.forEach { player ->
                    val newScore = recalculated.scores[player.id] ?: 0
                    if (newScore != player.score) {
                        playersCol(sessionId).document(player.id).updateFields {
                            "score" to newScore
                        }
                    }
                }
                sessionDoc(sessionId).updateFields { "potAmount" to recalculated.currentPot }
            }
            return
        }

        val bet = if (round.bet > 0) round.bet else
            sessionDoc(sessionId).get(Source.CACHE).data<SessionFirestore>().currentBet
        val deltas = ScoreCalculator.calculateDeltas(round, players, bet)

        writeScope.launch {
            roundsCol(sessionId).document(round.id).delete()
            players.forEach { player ->
                val delta = deltas[player.id] ?: 0
                if (delta != 0) {
                    playersCol(sessionId).document(player.id).updateFields {
                        "score" to (player.score - delta)
                    }
                }
            }
        }
    }

    override suspend fun updateBet(sessionId: String, newBet: Int) {
        val normalizedBet = normalizeBetToPowerOfTwo(newBet)
        writeScope.launch { sessionDoc(sessionId).updateFields { "currentBet" to normalizedBet } }
    }

    override suspend fun endSession(sessionId: String) {
        writeScope.launch { sessionDoc(sessionId).updateFields { "status" to "FINISHED" } }
    }

    override suspend fun reactivateSession(sessionId: String) {
        writeScope.launch { sessionDoc(sessionId).updateFields { "status" to "ACTIVE" } }
    }

    override suspend fun deleteSession(sessionId: String) {
        // Best-effort cleanup of subcollections. If cache does not have all docs,
        // deleting the root session is still enough to hide the session in app UI.
        val playerIds = runCatching {
            playersCol(sessionId).get(Source.CACHE).documents.map { it.id }
        }.getOrElse { emptyList() }
        val roundIds = runCatching {
            roundsCol(sessionId).get(Source.CACHE).documents.map { it.id }
        }.getOrElse { emptyList() }

        writeScope.launch {
            val batch = db.batch()
            playerIds.forEach { playerId ->
                batch.delete(playersCol(sessionId).document(playerId))
            }
            roundIds.forEach { roundId ->
                batch.delete(roundsCol(sessionId).document(roundId))
            }
            batch.delete(sessionDoc(sessionId))
            batch.commit()
        }

        knownSessionIds.remove(sessionId)
    }

    override suspend fun removeKnownSession(sessionId: String) {
        knownSessionIds.remove(sessionId)
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    private fun generateUuid(): String {
        // Simple UUID v4 using random bytes
        val bytes = ByteArray(16).also { platformRandomBytes(it) }
        bytes[6] = ((bytes[6].toInt() and 0x0f) or 0x40).toByte()
        bytes[8] = ((bytes[8].toInt() and 0x3f) or 0x80).toByte()
        return bytes.toHex().let {
            "${it.substring(0, 8)}-${it.substring(8, 12)}-${it.substring(12, 16)}-${it.substring(16, 20)}-${it.substring(20)}"
        }
    }

    private fun ByteArray.toHex() = joinToString("") { it.toInt().and(0xFF).toString(16).padStart(2, '0') }
}

// ── Firestore DTOs ───────────────────────────────────────────────────────────

@kotlinx.serialization.Serializable
data class SessionFirestore(
    val name: String = "",
    val baseBet: Int = 1,
    val currentBet: Int = 1,
    val potEnabled: Boolean = false,
    val potAmount: Int = 0,
    val status: String = "ACTIVE",
    val createdAt: Double = 0.0,
    val playerIds: List<String> = emptyList(),
) {
    fun toSession(id: String) = Session(
        id = id,
        name = name,
        baseBet = baseBet,
        currentBet = currentBet,
        potEnabled = potEnabled,
        potAmount = potAmount,
        status = if (status == "FINISHED") SessionStatus.FINISHED else SessionStatus.ACTIVE,
        createdAt = createdAt.toLong(),
        playerIds = playerIds,
    )
}

@kotlinx.serialization.Serializable
data class PlayerFirestore(
    val name: String = "",
    val color: String = "#E53935",
    val score: Int = 0,
) {
    fun toPlayer(id: String) = Player(id = id, name = name, color = color, score = score)
}

@kotlinx.serialization.Serializable
data class RoundFirestore(
    val actorId: String = "",
    val suit: String = "",
    val gameType: String = "",
    val flekLevel: Int = 0,
    val won: Boolean = false,
    val timestamp: Double = 0.0,
    val bet: Int = 0,
    val noCaller: Boolean = false,
) {
    fun toRound(id: String, sessionId: String) = Round(
        id = id,
        sessionId = sessionId,
        actorId = actorId,
        suit = cz.lhoracek.mapl.data.model.Suit.valueOf(suit),
        gameType = cz.lhoracek.mapl.data.model.GameType.valueOf(gameType),
        flekLevel = flekLevel,
        won = won,
        timestamp = timestamp.toLong(),
        bet = bet,
        noCaller = noCaller,
    )

    companion object {
        fun fromRound(r: Round) = RoundFirestore(
            actorId = r.actorId,
            suit = r.suit.name,
            gameType = r.gameType.name,
            flekLevel = r.flekLevel,
            won = r.won,
            timestamp = r.timestamp.toDouble(),
            bet = r.bet,
            noCaller = r.noCaller,
        )
    }
}

// ── Expect platform functions ─────────────────────────────────────────────────

expect fun platformRandomBytes(bytes: ByteArray)
